import React from 'react';
import { FormattedDate, FormattedNumber } from 'react-intl';
import { styled } from '@linaria/react';
import { toggle } from 'opticks';
import { BottomSheet } from '@daedalus/atlas/BottomSheet';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Divider } from '@daedalus/atlas/Divider';
import { Box } from '@daedalus/atlas/helpers/Box';
import { Icon } from '@daedalus/atlas/Icon';
import { PopUp } from '@daedalus/atlas/PopUp';
import { Tag } from '@daedalus/atlas/Tag';
import { Text } from '@daedalus/atlas/Text';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { formatTimeZone, getDateTimeAndTimezone } from '@daedalus/core/src/offer/business/cancellationPenalties';
import { CancellationPenalty } from '@daedalus/core/src/offer/types/offer';
import { convertToDifferentTimeZone } from '@daedalus/core/src/utils/date';
import { getChargeLaterDifferentialPrice, getTotalChargeLaterDifferentialPrice } from './utils';
interface Props {
  chargeDate: Date;
  setIsChargeLaterDetailsOpen: (state: boolean) => void;
  isChargeLaterDetailsOpen: boolean;
  currencyCode: string;
  totalUpFrontDisplayPrice?: number | null;
  payAtPropertyPrice?: number;
  cancellationPenalties: CancellationPenalty[];
  hotelTimeZone?: string;
}
const ChargeLaterWrapper = /*#__PURE__*/styled('div')({
  name: "ChargeLaterWrapper",
  class: "c1po3dsd",
  propsAsIs: false
});
export const ChargeLaterDetailsPopUp = ({
  chargeDate,
  setIsChargeLaterDetailsOpen,
  isChargeLaterDetailsOpen,
  totalUpFrontDisplayPrice,
  currencyCode,
  hotelTimeZone
}: Props) => {
  const {
    isMobile
  } = useDeviceLayout();
  if (!totalUpFrontDisplayPrice) return null;
  const headerTitle = <Text variant="titleM">
      {toggle('50feb427-cl-differential-price', <FormattedMessageWrapper id="payment.payLater" defaultMessage="Book now, pay later" />, <FormattedMessageWrapper id="chargeLater.label" defaultMessage="Pay now or later" />)}
    </Text>;
  const isUpdatePaymentTimelineOnBSide = toggle('3c85ed3b-update-payment-timeline-v2', false, true);
  const formattedChargeDate = <FormattedDate value={chargeDate} month="short" day="2-digit" />;
  const formattedChargeDateWithYear = <FormattedDate value={chargeDate} day="2-digit" month="short" year="numeric" />;
  const formattedTimeZone = formatTimeZone(getDateTimeAndTimezone(convertToDifferentTimeZone(chargeDate.toISOString(), hotelTimeZone || 'Etc/UTC')).timeZone);
  const formattedChargeDateAndTime = <>
      <FormattedMessageWrapper defaultMessage="{time} (GMT{timezone})" id="cancellationPenalties.time" values={{
      time: <FormattedDate value={chargeDate} month="short" day="2-digit" year="numeric" hour="numeric" minute="numeric" hourCycle="h23" timeZone={hotelTimeZone} />,
      timezone: formattedTimeZone
    }} />
    </>;
  const content = <div data-id="chargeLaterPopup">
      <Box paddingBottom="s400">
        <Text variant="labelM">
          <FormattedMessageWrapper id="chargeLater.zeroRiskStay" defaultMessage="Book your stay with zero risk." />
        </Text>
      </Box>
      <Text variant="bodyM" dataId="chargeLaterPopupDescription">
        <FormattedMessageWrapper id="chargeLater.zeroRiskStayDescription" defaultMessage="We’ll secure your stay today, then charge you on {chargeDate}. You're free to cancel at no cost until then." values={{
        chargeDate: formattedChargeDate
      }} />
      </Text>
    </div>;
  const rows = [{
    title: <FormattedMessageWrapper id="paymentType.payNow" defaultMessage="Pay now" />,
    list: [<FormattedMessageWrapper id="chargeLater.chargeToday" defaultMessage="You’ll be charged today." key="chargeToday" />],
    price: totalUpFrontDisplayPrice
  }, {
    title: <FormattedMessageWrapper id="chargeLater.PayLaterOn" defaultMessage="Pay later on {chargeDate}" values={{
      chargeDate: formattedChargeDate
    }} />,
    list: [<FormattedMessageWrapper id="chargeLater.flexibleOption" defaultMessage="Most flexible option." key="flexibleOption" />, <FormattedMessageWrapper id="chargeLater.zeroRisk" defaultMessage="Zero risk – you pay nothing today." key="zeroRisk" />, <FormattedMessageWrapper id="chargeLater.chargedOnDate" defaultMessage="You’ll be charged on {chargedOnDate}." values={{
      chargedOnDate: formattedChargeDateWithYear
    }} key="chargedOnDate" />],
    price: getTotalChargeLaterDifferentialPrice(totalUpFrontDisplayPrice),
    differentialPrice: getChargeLaterDifferentialPrice(totalUpFrontDisplayPrice)
  }];
  const clDifferentialPriceContent = <Box display="flex" flexDirection="column" gap="s500">
      <Box paddingTop={isMobile ? 's500' : 's000'}>
        <Text variant="titleM">
          <FormattedMessageWrapper id="chargeLater.chooseWhenToPay" defaultMessage="You can choose when you’d like to pay:" />
        </Text>
      </Box>
      <Box data-id="chargeLaterPopup" borderRadius="lg" borderColor="border.neutral.c100" borderWidth="s100" borderStyle="solid" padding="s500">
        {rows.map((row, index) => <>
            <Box display="flex" flexDirection="column" gap="s250">
              <Box display="flex" alignItems="center" justifyContent="flex-start" gap="s200">
                <Text variant="labelM">{row.title}</Text>
                {row.differentialPrice && <Tag>
                    <FormattedMessageWrapper id="chargeLater.differentialPriceAmount" defaultMessage="+ {amount}" values={{
                amount: <FormattedNumber value={row.differentialPrice} currency={currencyCode} style="currency" currencyDisplay={isUpdatePaymentTimelineOnBSide ? 'code' : 'symbol'} />
              }} />
                  </Tag>}
              </Box>

              <Box display="flex" flexDirection="column" gap="s200">
                {row.list.map((listItem, index) => <Box display="flex" alignItems="center" justifyContent="flex-start" gap="s200" key={index}>
                    <Icon name="Checkmark" colorPath="content.success.c600" />
                    <Text variant="bodyS">{listItem}</Text>
                  </Box>)}
              </Box>
              <Box paddingLeft="s600">
                <Text variant="titleS">
                  <FormattedMessageWrapper id="totalAmount" defaultMessage="{amount} total" values={{
                amount: <FormattedNumber value={row.price} currency={currencyCode} style="currency" currencyDisplay={isUpdatePaymentTimelineOnBSide ? 'code' : 'symbol'} />
              }} />
                </Text>
              </Box>
            </Box>
            {index !== rows.length - 1 && <Box paddingTop="s500" paddingBottom="s500">
                <Divider />
              </Box>}
          </>)}
      </Box>
      <Box display="flex" alignItems="flex-start" justifyContent="flex-start" gap="s200" paddingBottom="s200">
        <Icon name="CalendarCheck4" colorPath="content.neutral.c600" />
        <Text variant="bodyS">
          <FormattedMessageWrapper id="chargeLater.payLaterOnChargeTime" defaultMessage="Pay now or later – cancel at no cost until {chargeDateAndTime}." values={{
          chargeDateAndTime: formattedChargeDateAndTime
        }} />
        </Text>
      </Box>
    </Box>;
  if (isMobile) {
    return <BottomSheet isOpen={isChargeLaterDetailsOpen} onClose={() => setIsChargeLaterDetailsOpen(false)} titleBarProps={{
      centerContent: headerTitle
    }}>
        {toggle('50feb427-cl-differential-price', content, clDifferentialPriceContent)}
      </BottomSheet>;
  }
  return <PopUp isOpen={isChargeLaterDetailsOpen} titleBarProps={{
    centerContent: headerTitle
  }} closeAction={() => setIsChargeLaterDetailsOpen(false)} width={520}>
      <ChargeLaterWrapper>
        {toggle('50feb427-cl-differential-price', content, clDifferentialPriceContent)}
      </ChargeLaterWrapper>
    </PopUp>;
};

require("./ChargeLaterPopUp.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ChargeLaterPopUp.tsx");